import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { PROTAX_CONFIG_ID } from './auth-config-ids';
import { injectAssistantSession } from './assistant-session';

/**
 * Thin wrapper over angular-auth-oidc-client. checkAuth() runs once in the app initializer (see app.config.ts).
 *
 * ⚠️ SVAKI poziv biblioteci navodi configId izričito. Bez toga se razrešava konfiguracija na INDEKSU 0 —
 * što danas slučajno jeste ProTax, ali bi svaka promena redosleda tiho preusmerila prijavu i odjavu na
 * asistenta. Izričit id je jedino što to čini nemogućim umesto malo verovatnim.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
    private readonly oidc = inject(OidcSecurityService);
    private readonly assistant = injectAssistantSession();
    readonly isAuthenticated = signal(false);
    private authorizeTriggered = false;

    async checkAuth(): Promise<boolean> {
        try {
            // ⚠️ checkAuthMultiple, ne checkAuth: jednokonfiguracijski checkAuth poredi `state` iz URL-a samo
            // sa prvom konfiguracijom i BACA kad povratni poziv pripada asistentu. Vraća NIZ i onda kad
            // postoji samo jedna konfiguracija — pa bi destrukturiranje objekta ovde tiho dalo `undefined`,
            // a to stiže i u produkciju gde kanarinca nema da se primeti.
            const results = await firstValueFrom(this.oidc.checkAuthMultiple());
            const protax = results.find((result) => result.configId === PROTAX_CONFIG_ID);
            const isAuthenticated = protax?.isAuthenticated ?? false;
            this.isAuthenticated.set(isAuthenticated);
            return isAuthenticated;
        } catch {
            // Authority unreachable → treat as signed-out (don't crash the shell).
            this.isAuthenticated.set(false);
            return false;
        }
    }

    login(): void {
        if (this.authorizeTriggered) return;
        this.authorizeTriggered = true;
        this.oidc.authorize(PROTAX_CONFIG_ID);
    }

    logout(): void {
        // ⚠️ Asistentska sesija se briše PRE odjave. `logoff` završava postavljanjem `location.href` na
        // endpoint izdavaoca, pa se kod sekvenciran posle njega ne izvršava pouzdano — čišćenje „posle"
        // ostavilo bi upravo onu zaostalu sesiju koju ovo treba da spreči.
        this.assistant?.logoutLocal();
        this.oidc.logoff(PROTAX_CONFIG_ID).subscribe();
    }

    async getAccessToken(): Promise<string> {
        return firstValueFrom(this.oidc.getAccessToken(PROTAX_CONFIG_ID));
    }
}
